import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'libs/Axios';
import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import {
  RequestCubeRestructureCheck,
  RequestRestructureCube,
  ResponseCubePendingDependencySync,
  ResponseCubeRestructureCheck,
  ResponseRestructureCube,
} from 'types/api/SystemTenant/AROCube/CubeRestructureTypes';
import { CUBE_BUILDER_QUERY_KEY } from './CubeBuilderService';
import { CUBE_DATA_ENTRY_QUERY_KEY } from 'services/v1/Tenant/AROCube/CubeDataEntryService';
import { CUBE_DATA_DIMENSIONAL_VIEW_KEY } from 'services/v1/Tenant/AROCube/CubeDimensionalViewService';

const path = '/api/v1/cube-definition/restucture';

export const CUBE_RESTRUCTURE_QUERY_KEY = {
  PENDING_DEPENDENCY_SYNC: 'CUBE_RESTRUCTURE_PENDING_DEPENDENCY_SYNC',
};

const pendingDependencySyncPath = (cubeId: string) => `${path}/${encodeURIComponent(cubeId)}/pending-dependency-sync`;

// Dependency sync rows that block restructuring this cube.
export function useGetCubePendingDependencySync(cubeId?: string) {
  return useQuery<ResponseCubePendingDependencySync, AxiosDefaultErrorEntity>(
    [CUBE_RESTRUCTURE_QUERY_KEY.PENDING_DEPENDENCY_SYNC, cubeId],
    () => axios.get(pendingDependencySyncPath(cubeId as string)).then((response) => response.data),
    { enabled: !!cubeId, retry: false }
  );
}

export function useRetryCubePendingDependencySync() {
  const queryClient = useQueryClient();
  return useMutation<ResponseCubePendingDependencySync, AxiosDefaultErrorEntity, string>(
    (cubeId) => axios.post(`${pendingDependencySyncPath(cubeId)}/retry`).then((response) => response.data),
    {
      retry: false,
      onSuccess: (result, cubeId) => {
        queryClient.setQueryData([CUBE_RESTRUCTURE_QUERY_KEY.PENDING_DEPENDENCY_SYNC, cubeId], result);
        // A successful sync recalculates the dependent cubes' data.
        if (!result.retry?.synced) return;
        [...Object.values(CUBE_DATA_ENTRY_QUERY_KEY), ...Object.values(CUBE_DATA_DIMENSIONAL_VIEW_KEY)].forEach((key) =>
          queryClient.invalidateQueries(key, { refetchActive: false })
        );
      },
    }
  );
}

export function useCubeRestructureApi() {
  const queryClient = useQueryClient();
  const refreshCube = (result: ResponseRestructureCube) => {
    if (result.status !== 'COMPLETED' && result.status !== 'CANCELLED') return;
    Object.values(CUBE_BUILDER_QUERY_KEY).forEach((key) => queryClient.invalidateQueries(key));
    [...Object.values(CUBE_DATA_ENTRY_QUERY_KEY), ...Object.values(CUBE_DATA_DIMENSIONAL_VIEW_KEY)].forEach((key) =>
      queryClient.invalidateQueries(key, { refetchActive: false })
    );
  };
  const check = useMutation<ResponseCubeRestructureCheck, AxiosDefaultErrorEntity, RequestCubeRestructureCheck>(
    (payload) => axios.post(`${path}-data-check`, payload).then((response) => response.data),
    { retry: false }
  );
  const execute = useMutation<ResponseRestructureCube, AxiosDefaultErrorEntity, RequestRestructureCube>(
    (payload) => axios.post(path, payload).then((response) => response.data),
    { retry: false, onSuccess: refreshCube }
  );
  const status = useMutation<ResponseRestructureCube, AxiosDefaultErrorEntity, string>(
    (id) => axios.get(`${path}/${encodeURIComponent(id)}`).then((response) => response.data),
    { retry: false, onSuccess: refreshCube }
  );
  const retry = useMutation<ResponseRestructureCube, AxiosDefaultErrorEntity, string>(
    (id) => axios.post(`${path}/${encodeURIComponent(id)}/retry`).then((response) => response.data),
    { retry: false, onSuccess: refreshCube }
  );
  const cancel = useMutation<ResponseRestructureCube, AxiosDefaultErrorEntity, string>(
    (id) => axios.post(`${path}/${encodeURIComponent(id)}/cancel`).then((response) => response.data),
    { retry: false, onSuccess: refreshCube }
  );
  return { check, execute, status, retry, cancel };
}
